import { faSync } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';
import IconButton from './ui/IconButton';

const VERSION_STORAGE_KEY = 'appVersion';

class IfmsVersion {
  getVersion = () => {
    return localStorage.getItem(VERSION_STORAGE_KEY) ?? '';
  };

  setVersion = (ver: string) => {
    localStorage.setItem(VERSION_STORAGE_KEY, ver);
  };
}

interface VersionContextType {
  needsRefresh: boolean;
  setNeedsRefresh: React.Dispatch<React.SetStateAction<boolean>>;
  checkForUpdates: () => Promise<boolean>;
}

const VersionContext = createContext<VersionContextType | undefined>(undefined);

export const VersionProvider = ({ children }: { children: ReactNode }) => {
  const version = new IfmsVersion();
  const [needsRefresh, setNeedsRefresh] = useState(false);

  const getServerVersion = async (): Promise<string> => {
    const res = await fetch('/version.json', { cache: 'no-store' });
    const data = await res.json();
    return data.version;
  };

  useEffect(() => {
    const init = async () => {
      const serverVersion = await getServerVersion();
      const clientVersion = version.getVersion();
      if (!clientVersion) {
        version.setVersion(serverVersion);
        return;
      }
      if (clientVersion !== serverVersion) {
        version.setVersion(serverVersion);
      }
    };

    init();
  }, []);

  const checkForUpdates = async (): Promise<boolean> => {
    const clientVersion = version.getVersion();
    const serverVersion = await getServerVersion();
    if (clientVersion !== serverVersion) {
      setNeedsRefresh(true);
      return false;
    }
    return true;
  };

  return (
    <VersionContext.Provider value={{ needsRefresh, setNeedsRefresh, checkForUpdates }}>
      {children}
      {needsRefresh && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', textAlign: 'center' }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <h2>A new version has been released.</h2>
            <p>Press the button below to refresh</p>
            <IconButton name='detail-screen-refresh' data-tooltip='Refresh' onClick={() => window.location.reload()}>
              <FontAwesomeIcon color='#47C83E' icon={faSync} />
            </IconButton>
          </div>
        </div>
      )}
    </VersionContext.Provider>
  );
};

export const useVersion = () => {
  const context = useContext(VersionContext);
  if (!context) {
    throw new Error('useVersion must be used within a VersionProvider');
  }
  return context;
};
